import { Search } from 'lucide-react';
import React, { useState } from 'react';

interface Interaction {
  id: string;
  contactName: string;
  accountName: string;
  type: string;
  date: string;
  summary: string;
  owner: string;
}

const mockInteractions: Interaction[] = [
  {
    id: '1',
    contactName: 'Edwin Costa',
    accountName: 'TechTory',
    type: 'Meeting',
    date: '2023-05-25T14:30:00.000Z',
    summary: 'Discussed design requirements for new project',
    owner: 'Jack Stunner'
  },
  {
    id: '2',
    contactName: 'Margot Buckley',
    accountName: 'Smarket',
    type: 'Email',
    date: '2023-05-26T09:15:00.000Z',
    summary: 'Sent marketing materials for upcoming campaign',
    owner: 'Jack Stunner'
  },
  {
    id: '3',
    contactName: 'William Case',
    accountName: 'Shopment',
    type: 'Call',
    date: '2023-05-26T11:00:00.000Z',
    summary: 'Quarterly account review',
    owner: 'Amy McKay'
  },
  {
    id: '4',
    contactName: 'Nathan Baker',
    accountName: 'Grocer Jar',
    type: 'Meeting',
    date: '2023-05-27T13:45:00.000Z',
    summary: 'Design review for packaging redesign',
    owner: 'Cynthia Simons'
  },
  {
    id: '5',
    contactName: 'Katerina Estes',
    accountName: 'Smarket',
    type: 'Call',
    date: '2023-05-28T10:30:00.000Z',
    summary: 'Strategic planning for Q3 marketing initiatives',
    owner: 'Jack Stunner'
  },
  {
    id: '6',
    contactName: 'Adrian Floyd',
    accountName: 'Wholesale Centers',
    type: 'Meeting',
    date: '2023-05-29T15:00:00.000Z',
    summary: 'Operations review and process improvement discussion',
    owner: 'Jayson Oneil'
  }
];

const Interactions: React.FC = () => {
  const [interactions] = useState<Interaction[]>(mockInteractions);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInteractions, setSelectedInteractions] = useState<string[]>([]);

  const filteredInteractions = interactions.filter(interaction =>
    interaction.contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    interaction.accountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    interaction.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
    interaction.owner.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = () => {
    if (selectedInteractions.length === filteredInteractions.length) {
      setSelectedInteractions([]);
    } else {
      setSelectedInteractions(filteredInteractions.map(interaction => interaction.id));
    }
  };

  const handleSelectInteraction = (id: string) => {
    if (selectedInteractions.includes(id)) {
      setSelectedInteractions(selectedInteractions.filter(interactionId => interactionId !== id));
    } else {
      setSelectedInteractions([...selectedInteractions, id]);
    }
  };

  const getTypeColor = (type: string) => {
    const colors = {
      'Meeting': 'bg-blue-100 text-blue-800',
      'Call': 'bg-green-100 text-green-800',
      'Email': 'bg-purple-100 text-purple-800',
      'Task': 'bg-yellow-100 text-yellow-800',
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="h-full bg-gray-50">
      <div className="flex flex-col overflow-hidden h-full">
        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-white shadow-sm rounded-lg h-full flex flex-col">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-semibold text-gray-900">Interactions</h1>
              </div>

              <div className="flex items-center space-x-4 my-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Search interactions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedInteractions.length === filteredInteractions.length && filteredInteractions.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Summary
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Owner
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInteractions.map((interaction) => (
                    <tr key={interaction.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedInteractions.includes(interaction.id)}
                          onChange={() => handleSelectInteraction(interaction.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-sm">{interaction.contactName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {interaction.accountName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(interaction.type)}`}>
                          {interaction.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {formatDate(interaction.date)}
                      </td>
                      <td className="px-6 py-4 text-sm max-w-xs truncate">
                        {interaction.summary}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {interaction.owner}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Interactions;
